import React, { Suspense, memo, useEffect } from "react";
import RazorpayService from "@/services/api/razorpay";
import { clearMySubscription, saveMySubscription } from "@/lib/utils";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import styles from "./PostLogin.module.scss";
import Navbar from "./components/Navbar/Navbar";
import ErrorBoundary from "@/components/ErrorBoundary/ErrorBoundary";
import Loader from "@/components/Loader/Loader";
import TitleUpdater from "@/components/TitleUpdater/TitleUpdater";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { NotificationProvider } from "@/contexts/NotificationContext";
import {
  CalendarNavIcon,
  ChatNavIcon,
  HomeNavIcon,
  ProfileNavIcon,
} from "@/components/SVGComponents/MemberIcons";
import routes from "@/routes/routes";

const NAV_ITEMS = [
  {
    label: "Home",
    path: routes.privateRoutes.HOME.path,
    icon: HomeNavIcon,
  },
  {
    label: "Chat",
    path: routes.privateRoutes.CHAT.path,
    icon: ChatNavIcon,
  },
  {
    label: "Event",
    path: routes.privateRoutes.EVENT.path,
    icon: CalendarNavIcon,
  },
  {
    label: "Profile",
    path: routes.privateRoutes.PROFILE.path,
    icon: ProfileNavIcon,
  },
] as const;

const isFullBleedPath = (pathname: string) =>
  /^\/(welcome|questions|introduction|how-it-works|dine-with-founders|membership|payment-successful)(\/|$)/.test(
    pathname,
  ) ||
  /^\/profile\/(manage-membership|change-membership)(\/|$)/.test(pathname);

const PostLogin: React.FC = () => {
  const { pathname } = useLocation();
  const hideAppChrome = isFullBleedPath(pathname);

  useEffect(() => {
    const fetchMySubscriptions = async () => {
      try {
        const response = await RazorpayService.mySubscriptions();

        if (!response?.success) return;

        if (response.data) {
          saveMySubscription(response.data);
        } else {
          clearMySubscription();
        }
      } catch (error) {
        console.error(error);
      }
    };

    fetchMySubscriptions();
  }, []);

  return (
    <LanguageProvider>
      <NotificationProvider>
        <div className={styles.rootContainer}>
          <TitleUpdater />

          {!hideAppChrome ? (
            <div className={styles.desktopNavbar}>
              <Navbar />
            </div>
          ) : null}

          <div
            className={`${styles.contentArea} ${
              hideAppChrome ? styles.contentAreaFullBleed : ""
            }`}
          >
            <Suspense fallback={<Loader />}>
              <ErrorBoundary>
                <Outlet />
              </ErrorBoundary>
            </Suspense>
          </div>

          {!hideAppChrome ? (
            <nav aria-label="Primary navigation" className={styles.mobileBottomNav}>
              {NAV_ITEMS.map(({ icon: Icon, label, path }) => (
                <NavLink
                  key={label}
                  className={({ isActive }) =>
                    `${styles.mobileBottomNavItem} ${
                      isActive ? styles.mobileBottomNavItemActive : ""
                    }`
                  }
                  to={path}
                >
                  <Icon className={styles.mobileBottomNavIcon} />
                  <span>{label}</span>
                </NavLink>
              ))}
            </nav>
          ) : null}
        </div>
      </NotificationProvider>
    </LanguageProvider>
  );
};

export default memo(PostLogin);
